import { z } from 'zod';
import { IdSchema, IsoDateTimeSchema, SlugSchema, nullableText, optionalText, paginated, requiredText, } from './common.js';
import { MediaTypeSchema, ProductStatusSchema, SizeClassSchema, SupplyTypeSchema, } from './enums.js';
import { MoneySchema, parseTiyin } from './money.js';
/* ============================== Kategoriya ============================== */
export const CategoryResponseSchema = z.object({
    id: IdSchema,
    parentId: IdSchema.nullable(),
    slug: z.string(),
    nameUz: z.string(),
    nameRu: nullableText(120),
    path: z.string(),
    depth: z.number().int().nonnegative(),
    iconKey: nullableText(32),
    /** Admin yuklagan rasm (WebP). `null` bo'lsa `iconKey` ikonkasi ko'rsatiladi. */
    imageUrl: nullableText(255),
    colorHex: nullableText(9),
    sort: z.number().int(),
    isActive: z.boolean(),
    productCount: z.number().int().nonnegative().optional(),
});
export const CategoryTreeResponseSchema = z.lazy(() => CategoryResponseSchema.extend({ children: z.array(CategoryTreeResponseSchema) }));
export const CreateCategorySchema = z
    .object({
    parentId: IdSchema.nullable().optional(),
    slug: SlugSchema.max(120),
    nameUz: requiredText(120),
    nameRu: optionalText(120),
    iconKey: optionalText(32),
    colorHex: optionalText(9),
    sort: z.number().int().min(0).max(9999).optional(),
})
    .strict();
export const UpdateCategorySchema = CreateCategorySchema.partial()
    .extend({ isActive: z.boolean().optional() })
    .strict();
/* ================================ Brend ================================ */
export const BrandResponseSchema = z.object({
    id: IdSchema,
    slug: z.string(),
    name: z.string(),
    country: nullableText(60),
    logoUrl: nullableText(255),
    sort: z.number().int(),
    isActive: z.boolean(),
});
export const CreateBrandSchema = z
    .object({
    slug: SlugSchema.max(80),
    name: requiredText(80),
    country: optionalText(60),
    logoUrl: optionalText(255),
    sort: z.number().int().min(0).max(9999).optional(),
})
    .strict();
export const UpdateBrandSchema = CreateBrandSchema.partial()
    .extend({ isActive: z.boolean().optional() })
    .strict();
/* ================================ Rasm ================================= */
export const ProductMediaResponseSchema = z.object({
    id: IdSchema,
    type: MediaTypeSchema,
    originalUrl: z.string(),
    thumbUrl: nullableText(255),
    cardUrl: nullableText(255),
    fullUrl: nullableText(255),
    isPrimary: z.boolean(),
    sort: z.number().int(),
    altUz: nullableText(160),
    altRu: nullableText(160),
});
/* =============================== Qoldiq ================================ */
/**
 * Do'kon bo'yicha qoldiq. Mijozga hech qachon "bor" deyilmaydi —
 * "QAYERDA bor" deyiladi (docs/02-MAHSULOT.md).
 */
export const ProductStockResponseSchema = z.object({
    shopId: IdSchema,
    shopCode: z.string(),
    shopNameUz: z.string(),
    shopNameRu: nullableText(120),
    qty: z.number().int().nonnegative(),
    /** Rezerv chegirilgan, mijozga ko'rinadigan son. */
    available: z.number().int().nonnegative(),
});
/* =============================== Mahsulot ============================== */
/** Ro'yxat va qidiruv natijasi uchun — indeksdan to'g'ridan-to'g'ri chiziladi. */
export const ProductListItemSchema = z.object({
    id: IdSchema,
    sku: z.string(),
    slug: z.string(),
    nameUz: z.string(),
    nameRu: nullableText(200),
    brandName: z.string(),
    categorySlug: z.string(),
    /** Rasm yo'q bo'lsa `null` — klient kategoriya ikonkasiga tushadi. */
    imageUrl: z.string().nullable(),
    categoryIconKey: nullableText(32),
    price: MoneySchema,
    oldPrice: MoneySchema.nullable(),
    /** "Tez kunda": narx ko'rsatilmaydi va tovar savatga tushmaydi. */
    comingSoon: z.boolean(),
    supplyType: SupplyTypeSchema,
    leadTimeDays: z.number().int().nonnegative(),
    sizeClass: SizeClassSchema,
    stock: z.array(ProductStockResponseSchema),
});
export const ProductResponseSchema = ProductListItemSchema.extend({
    descUz: nullableText(20_000),
    descRu: nullableText(20_000),
    fitsUz: nullableText(500),
    fitsRu: nullableText(500),
    oemCodes: nullableText(2000),
    searchTags: nullableText(4000),
    status: ProductStatusSchema,
    publishedAt: IsoDateTimeSchema.nullable(),
    media: z.array(ProductMediaResponseSchema),
    salesCount: z.number().int().nonnegative(),
    /** Faqat `report.finance` ruxsati borlarga qaytariladi. */
    costPrice: MoneySchema.optional(),
});
export const CreateProductSchema = z
    .object({
    sku: z.string().trim().min(1).max(48),
    slug: SlugSchema.max(160),
    brandId: IdSchema,
    categoryId: IdSchema,
    nameUz: requiredText(200),
    nameRu: optionalText(200),
    descUz: optionalText(20_000),
    descRu: optionalText(20_000),
    fitsUz: optionalText(500),
    fitsRu: optionalText(500),
    oemCodes: optionalText(2000),
    /** BITTA umumiy maydon — tilga bo'linmaydi (avtopart-i18n skill). */
    searchTags: optionalText(4000),
    /** "Tez kunda" bo'lsa narx majburiy emas (0 saqlanadi). */
    price: MoneySchema.optional(),
    oldPrice: MoneySchema.optional(),
    costPrice: MoneySchema.optional(),
    comingSoon: z.boolean().default(false),
    supplyType: SupplyTypeSchema.default('IN_STOCK'),
    leadTimeDays: z.number().int().min(0).max(365).default(0),
    sizeClass: SizeClassSchema.default('NORMAL'),
    status: ProductStatusSchema.default('DRAFT'),
})
    .strict()
    .refine((v) => v.supplyType !== 'PRE_ORDER' || v.leadTimeDays > 0, {
    message: "PRE_ORDER tovarida kutish muddati ko'rsatilishi shart",
    path: ['leadTimeDays'],
})
    .refine((v) => v.comingSoon || v.price !== undefined, {
    message: "Narx majburiy (faqat 'Tez kunda' tovarda bo'sh bo'lishi mumkin)",
    path: ['price'],
})
    .refine((v) => {
    if (v.oldPrice === undefined || v.price === undefined)
        return true;
    const oldPrice = parseTiyin(v.oldPrice);
    const price = parseTiyin(v.price);
    // Qiymatlardan biri noto'g'ri bo'lsa, o'z schemasi allaqachon xato
    // qo'shgan — bu yerda ikkinchi xato qo'shilmaydi.
    if (oldPrice === null || price === null)
        return true;
    return oldPrice > price;
}, {
    message: "Eski narx joriy narxdan yuqori bo'lishi kerak",
    path: ['oldPrice'],
});
/**
 * Tahrirlash. `sku` o'zgarmaydi — u tashqi tizimlar va importning kaliti.
 * Narx alohida ruxsat talab qiladi (`product.edit_price`), shuning uchun
 * server uni alohida tekshiradi.
 */
export const UpdateProductSchema = z
    .object({
    brandId: IdSchema.optional(),
    categoryId: IdSchema.optional(),
    nameUz: requiredText(200).optional(),
    nameRu: z.string().trim().max(200).nullable().optional(),
    descUz: z.string().trim().max(20_000).nullable().optional(),
    descRu: z.string().trim().max(20_000).nullable().optional(),
    fitsUz: z.string().trim().max(500).nullable().optional(),
    fitsRu: z.string().trim().max(500).nullable().optional(),
    oemCodes: z.string().trim().max(2000).nullable().optional(),
    searchTags: z.string().trim().max(4000).nullable().optional(),
    price: MoneySchema.optional(),
    oldPrice: MoneySchema.nullable().optional(),
    costPrice: MoneySchema.optional(),
    comingSoon: z.boolean().optional(),
    supplyType: SupplyTypeSchema.optional(),
    leadTimeDays: z.number().int().min(0).max(365).optional(),
    sizeClass: SizeClassSchema.optional(),
    status: ProductStatusSchema.optional(),
    sortWeight: z.number().int().min(-1000).max(1000).optional(),
})
    .strict();
/** Do'kon qoldig'ini qo'lda tuzatish — har o'zgarish `StockMove` yozadi. */
export const AdjustStockSchema = z
    .object({
    shopId: IdSchema,
    qty: z.number().int(),
    note: z.string().trim().min(1).max(255),
})
    .strict();
export const ListProductsQuerySchema = z
    .object({
    categoryId: IdSchema.optional(),
    brandId: IdSchema.optional(),
    supplyType: SupplyTypeSchema.optional(),
    status: ProductStatusSchema.optional(),
    shopId: IdSchema.optional(),
    inStockOnly: z.coerce.boolean().optional(),
    sort: z.enum(['popular', 'price_asc', 'price_desc', 'new']).default('popular'),
    limit: z.coerce.number().int().min(1).max(100).default(24),
    cursor: z.string().max(255).optional(),
})
    .strict();
/* ========================= admin katalog ro'yxati ========================= */
/**
 * Admin tovar ro'yxati.
 *
 * Mijoz ro'yxatidan (`ProductListItem`) ATAYLAB alohida:
 *
 *  1. Unda `status` yo'q — mijozga faqat `ACTIVE` beriladi, shuning
 *     uchun maydon keraksiz edi. Adminga esa u ENG MUHIM ustun: Excel
 *     importidan keyin yuzlab tovar `DRAFT` bo'lib tushadi va ular
 *     ro'yxatda faol tovardan farqlanishi shart (TASK-008e).
 *  2. Mijoz ro'yxatida rasm, chegirma foizi va do'kon qoldig'i bor —
 *     bular kartochka uchun. Adminga tahrirlash uchun kerakli maydonlar
 *     boshqacha: qaysi kategoriya, qachon o'zgargan, jami qoldiq.
 */
export const AdminProductListItemSchema = z.object({
    id: IdSchema,
    sku: z.string(),
    slug: z.string(),
    nameUz: z.string(),
    nameRu: nullableText(200),
    brandName: z.string(),
    categoryNameUz: z.string(),
    /** Ro'yxatda kategoriya ikonkasi ko'rsatiladi — demodagi kabi. */
    categoryIconKey: nullableText(32),
    status: ProductStatusSchema,
    price: MoneySchema,
    oldPrice: MoneySchema.nullable(),
    comingSoon: z.boolean(),
    supplyType: SupplyTypeSchema,
    leadTimeDays: z.number().int().nonnegative(),
    /** Barcha do'konlardagi jami qoldiq — qaysi do'kon ekani ro'yxatda kerak emas. */
    stockTotal: z.number().int().nonnegative(),
    hasImage: z.boolean(),
    updatedAt: IsoDateTimeSchema,
});
export const AdminProductsResponseSchema = paginated(AdminProductListItemSchema);
/**
 * Admin ro'yxati so'rovi.
 *
 * `query` — Meilisearch EMAS, oddiy bazadagi moslik. Sabab: indeksda
 * faqat `ACTIVE` tovarlar bor (`search-index.service.ts`), demak
 * `DRAFT` tovarni qidiruv orqali umuman topib bo'lmaydi. Admin uchun
 * imlo tuzatish kerak emas — u artikulni aniq biladi.
 */
export const ListAdminProductsQuerySchema = z
    .object({
    /** Artikul, nom yoki OEM raqami bo'yicha moslik. */
    query: z.string().trim().max(120).optional(),
    status: ProductStatusSchema.optional(),
    categoryId: IdSchema.optional(),
    brandId: IdSchema.optional(),
    supplyType: SupplyTypeSchema.optional(),
    /** Standart — oxirgi tahrirlangan birinchi: import qilingandan keyin ish shu yerda. */
    sort: z.enum(['updated', 'name', 'price_asc', 'price_desc']).default('updated'),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(255).optional(),
})
    .strict();
/**
 * Qoralamalarni ommaviy faollashtirish.
 *
 * Excel importi yangi tovarni `DRAFT` qiladi (mijozga ko'rinmaydi), yuzlab
 * tovarni bittalab ochish esa amalda mumkin emas. Filtr admin ro'yxatidagi
 * bilan BIR XIL: operator ekranda ko'rgan tovarlar faollashadi. Filtrsiz
 * so'rov ham to'g'ri — "hamma qoralama".
 *
 * `status` YO'Q: bu amal faqat `DRAFT` ga ishlaydi. Arxivlangan tovar
 * ataylab qayta ochilmaydi.
 */
export const ActivateDraftsSchema = z
    .object({
    query: z.string().trim().max(120).optional(),
    categoryId: IdSchema.optional(),
    brandId: IdSchema.optional(),
    supplyType: SupplyTypeSchema.optional(),
})
    .strict();
/** Bir so'rovda ko'pi bilan shuncha tovar faollashadi; qolgani `remaining` da. */
export const ACTIVATE_DRAFTS_LIMIT = 2000;
export const ActivateDraftsResponseSchema = z.object({
    /** Faol bo'ldi. */
    activated: z.number().int().nonnegative(),
    /** Qoralamada QOLDI: narxi nol yoki buyurtma asosidagi tovarda muddat yo'q. */
    skipped: z.number().int().nonnegative(),
    /** Chegaradan oshgani uchun bu safar tegilmagan — yana bosish kerak. */
    remaining: z.number().int().nonnegative(),
});
//# sourceMappingURL=catalog.js.map